(() => {
  const promotion = window.theme && window.theme.giftPromotion;

  if (!promotion || !window.fetch || !window.DOMParser) return;

  class CartGiftPromotion {
    constructor() {
      this.variantId = String(promotion.variantId || '').trim();
      this.validVariantId = /^\d+$/.test(this.variantId);
      this.thresholdCents = Number(promotion.thresholdCents) || 50000;
      this.marker = promotion.marker || '_miva_gift';
      this.inFlight = false;
      this.pendingCart = null;
      this.stockUnavailable = false;

      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);
      this.onAjaxProductAdded = this.onAjaxProductAdded.bind(this);

      document.addEventListener('ajaxProduct:added', this.onAjaxProductAdded);
      document.addEventListener('cart:updated', this.onCartUpdated);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }

      this.fetchCart().catch(() => {});
    }

    onCartUpdate(event) {
      if (event && event.cart) this.handleCart(event.cart, true);
    }

    onCartUpdated(event) {
      if (event && event.detail && event.detail.cart) this.handleCart(event.detail.cart, true);
    }

    onAjaxProductAdded(event) {
      const cart = event && event.detail && event.detail.product;
      if (cart) this.handleCart(cart, true);
    }

    async fetchCart() {
      const response = await fetch(`${theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Não foi possível consultar o carrinho.');

      const cart = await response.json();
      this.handleCart(cart, false);
      return cart;
    }

    handleCart(cart, resetStockState) {
      if (!cart || !Array.isArray(cart.items)) return;

      if (resetStockState) this.stockUnavailable = false;

      if (this.inFlight) {
        this.pendingCart = cart;
        this.updateProgress(cart);
        return;
      }

      this.reconcile(cart);
    }

    getMarkedGiftItems(cart) {
      return cart.items
        .map((item, index) => ({ ...item, line: index + 1 }))
        .filter((item) => item.properties && item.properties[this.marker] === 'true');
    }

    getQualifyingTotal(cart) {
      return cart.items.reduce((total, item) => {
        if (item.properties && item.properties[this.marker] === 'true') return total;

        return total + Number(item.original_line_price ?? item.line_price ?? 0);
      }, 0);
    }

    isEligible(qualifyingTotal) {
      return Boolean(
        promotion.enabled &&
        this.validVariantId &&
        qualifyingTotal >= this.thresholdCents
      );
    }

    reconcile(cart) {
      const giftItems = this.getMarkedGiftItems(cart);
      const qualifyingTotal = this.getQualifyingTotal(cart);
      const eligible = this.isEligible(qualifyingTotal);
      const hasWrongVariant = giftItems.some((item) => String(item.variant_id) !== this.variantId);

      this.updateProgress(cart, { hasGift: giftItems.length > 0 && !hasWrongVariant });

      if (!eligible) {
        if (giftItems.length) this.removeGiftItems(cart, giftItems);
        return;
      }

      if (hasWrongVariant) {
        this.removeGiftItems(cart, giftItems);
        return;
      }

      if (giftItems.length === 0) {
        if (!this.stockUnavailable) this.addGift(cart);
        return;
      }

      if (giftItems.length !== 1 || Number(giftItems[0].quantity) !== 1) {
        this.normalizeGiftItems(cart, giftItems);
      }
    }

    async addGift(cart) {
      if (this.inFlight) return;

      this.inFlight = true;
      this.updateProgress(cart, { adding: true });

      try {
        const state = await this.requestAddGift();
        this.stockUnavailable = false;
        this.renderSections(state);
        this.updateProgress(state, { hasGift: true });
      } catch (error) {
        this.stockUnavailable = true;
        this.updateProgress(cart, { stockUnavailable: true });
        console.warn('[Miva] Porta-joias indisponível ou não pôde ser adicionado.', error);
      } finally {
        await this.finishOperation(cart);
      }
    }

    async removeGiftItems(cart, giftItems) {
      if (this.inFlight) return;

      this.inFlight = true;
      let state = cart;
      const sortedItems = [...giftItems].sort((a, b) => b.line - a.line);

      try {
        for (let index = 0; index < sortedItems.length; index += 1) {
          state = await this.requestChange(
            sortedItems[index].line,
            0,
            index === sortedItems.length - 1
          );
        }

        this.renderSections(state);
        this.updateProgress(state);
      } catch (error) {
        console.warn('[Miva] Não foi possível remover o porta-joias promocional.', error);
      } finally {
        await this.finishOperation(state);
      }
    }

    async normalizeGiftItems(cart, giftItems) {
      if (this.inFlight) return;

      this.inFlight = true;
      let state = cart;
      const sortedItems = [...giftItems].sort((a, b) => a.line - b.line);
      const keeper = sortedItems[0];

      try {
        for (let index = sortedItems.length - 1; index > 0; index -= 1) {
          state = await this.requestChange(
            sortedItems[index].line,
            0,
            index === 1 && Number(keeper.quantity) === 1
          );
        }

        if (Number(keeper.quantity) !== 1) {
          state = await this.requestChange(keeper.line, 1, true);
        }

        this.renderSections(state);
        this.updateProgress(state, { hasGift: true });
      } catch (error) {
        console.warn('[Miva] Não foi possível limitar o porta-joias promocional a 1 unidade.', error);
      } finally {
        await this.finishOperation(state);
      }
    }

    async finishOperation(fallbackCart) {
      this.inFlight = false;
      this.pendingCart = null;

      try {
        await this.fetchCart();
      } catch (error) {
        if (fallbackCart) this.handleCart(fallbackCart, false);
      }
    }

    getSectionsToRender() {
      const sections = [];
      const addSection = (id, section, selector) => {
        if (!section || !document.getElementById(id)) return;
        if (sections.some((item) => item.section === section)) return;
        sections.push({ id, section, selector });
      };

      addSection('mini-cart', 'mini-cart', '.shopify-section');
      addSection(
        'main-cart-items',
        document.getElementById('main-cart-items')?.dataset.id,
        '.js-contents'
      );
      addSection(
        'main-cart-footer',
        document.getElementById('main-cart-footer')?.dataset.id,
        '.cart__footer'
      );
      addSection('cart-icon-bubble', 'cart-icon-bubble', '.shopify-section');
      addSection('mobile-cart-icon-bubble', 'mobile-cart-icon-bubble', '.shopify-section');
      addSection('cart-live-region-text', 'cart-live-region-text', '.shopify-section');

      return sections;
    }

    getSectionIds() {
      return this.getSectionsToRender().map((section) => section.section);
    }

    getRequestConfig(body) {
      return {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      };
    }

    async requestAddGift() {
      const body = {
        items: [
          {
            id: Number(this.variantId),
            quantity: 1,
            properties: {
              [this.marker]: 'true',
              _miva_gift_promotion: 'porta-joias',
            },
          },
        ],
        sections: this.getSectionIds(),
        sections_url: window.location.pathname,
      };
      const response = await fetch(theme.routes.cart_add_url, this.getRequestConfig(body));
      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(state.description || 'O brinde não está disponível.');
      }

      return state;
    }

    async requestChange(line, quantity, includeSections) {
      const body = { line, quantity };

      if (includeSections) {
        body.sections = this.getSectionIds();
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(theme.routes.cart_change_url, this.getRequestConfig(body));
      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(state.description || 'Não foi possível atualizar o carrinho.');
      }

      return state;
    }

    renderSections(state) {
      if (!state || !state.sections) return;

      this.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        const html = state.sections[section.section];
        if (!element || typeof html !== 'string') return;

        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const sectionContent = parsedDocument.querySelector(section.selector);
        if (!sectionContent) return;

        if (section.selector === '.shopify-section') {
          element.innerHTML = sectionContent.innerHTML;
        } else {
          const target = element.querySelector(section.selector);
          if (target) target.innerHTML = sectionContent.innerHTML;
        }
      });
    }

    updateProgress(cart, state = {}) {
      const progressElement = document.querySelector('[data-gift-promotion]');
      if (!progressElement || !cart || !Array.isArray(cart.items)) return;

      const qualifyingTotal = this.getQualifyingTotal(cart);
      const progress = Math.min(100, Math.max(0, (qualifyingTotal / this.thresholdCents) * 100));
      const progressValue = Math.min(this.thresholdCents, Math.max(0, qualifyingTotal));
      const hasGift = Boolean(state.hasGift || this.getMarkedGiftItems(cart).length);
      const adding = Boolean(state.adding);
      const stockUnavailable = Boolean(state.stockUnavailable || this.stockUnavailable);
      const messageElement = progressElement.querySelector('[data-gift-promotion-message]');
      const fillElement = progressElement.querySelector('[data-gift-promotion-fill]');
      const meterElement = progressElement.querySelector('[role="progressbar"]');

      progressElement.classList.toggle('is-complete', hasGift);
      progressElement.classList.toggle('is-unavailable', stockUnavailable);
      progressElement.dataset.qualifyingTotalCents = String(qualifyingTotal);

      if (fillElement) fillElement.style.width = `${progress}%`;
      if (meterElement) meterElement.setAttribute('aria-valuenow', String(progressValue));

      if (!messageElement) return;

      if (stockUnavailable) {
        messageElement.textContent = 'No momento, o porta-joias está indisponível em estoque.';
      } else if (hasGift) {
        messageElement.textContent = 'Porta-joias adicionado ao carrinho.';
      } else if (qualifyingTotal >= this.thresholdCents || adding) {
        messageElement.textContent = 'Seu brinde está sendo preparado.';
      } else {
        messageElement.innerHTML = `Faltam <strong>${this.formatMoney(this.thresholdCents - qualifyingTotal)}</strong> para ganhar seu porta-joias.`;
      }
    }

    formatMoney(cents) {
      const currency = (theme.shopSettings && theme.shopSettings.isoCode) || 'BRL';

      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
      }).format(Math.max(0, cents) / 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CartGiftPromotion(), { once: true });
  } else {
    new CartGiftPromotion();
  }
})();
